import React from "react";

const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Characteristic Roots: three damping cases
//   y'' + 2γy' + ω₀²y = 0  shown with phase portraits
// ─────────────────────────────────────────────────────────────────
function DiagramCharacteristicRoots() {
  const W = 900, H = 340;
  // Three panels: overdamped, critically damped, underdamped
  const panW = (W - 28) / 3;

  const cases = [
    {
      label: 'Overdamped',
      sub:   'γ > ω₀  →  two real roots',
      color: '#c0392b', fill: '#fff0f0',
      // γ=2, ω₀=1 → r = -2 ± √3  ≈ -0.27, -3.73
      r1: -2 + Math.sqrt(3), r2: -2 - Math.sqrt(3),
      desc: ['r₁,r₂ real, negative', 'No oscillation', 'Slow return to zero'],
      rootLabel: `r₁≈${(-2+Math.sqrt(3)).toFixed(2)}, r₂≈${(-2-Math.sqrt(3)).toFixed(2)}`,
    },
    {
      label: 'Critically Damped',
      sub:   'γ = ω₀  →  repeated root',
      color: '#e07b00', fill: '#fff7ee',
      // γ=ω₀=2 → r = -2 (double)
      r1: -2, r2: -2,
      desc: ['r = −γ  (double)', 'Fastest return to 0', 'No oscillation'],
      rootLabel: 'r = −2  (repeated)',
    },
    {
      label: 'Underdamped',
      sub:   'γ < ω₀  →  complex roots',
      color: '#2E75B6', fill: '#EBF3FB',
      // γ=0.4, ω₀=2 → r = -0.4 ± i√(4-0.16) = -0.4 ± i·1.96
      r1: null, r2: null,
      gamma: 0.4, omega: 2,
      desc: ['r = −γ ± iω_d', 'Oscillates, decays', 'ω_d = √(ω₀²−γ²)'],
      rootLabel: 'r = −0.4 ± 1.96i',
    },
  ];

  const aH = 160, aYoff = 52, tMax = 6;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Second-Order Homogeneous ODE — y″ + 2γy′ + ω₀²y = 0 · Three Damping Cases
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {cases.map(({ label, sub, color, fill, r1, r2, gamma, omega, desc, rootLabel }, pi) => {
          const px   = 10 + pi * (panW + 4);
          const aX   = px + 36;
          const aY   = aYoff;
          const aW   = panW - 50;
          const tMin = 0;
          const yMin = -1.5, yMax = 2.2;
          const toX  = t => aX + (t - tMin) / (tMax - tMin) * aW;
          const toY  = y => aY + aH - (y - yMin) / (yMax - yMin) * aH;

          // Compute solution curve y(t), y(0)=2, y'(0)=0
          const y0sol = 2;
          let yFn;
          if (pi === 0) {
            // y = C1·e^(r1·t) + C2·e^(r2·t);  y(0)=2, y'(0)=0
            // C1+C2=2, C1·r1+C2·r2=0 → C1=2r2/(r2-r1), C2=2-C1
            const C1 = 2 * r2 / (r2 - r1);
            const C2 = y0sol - C1;
            yFn = t => C1 * Math.exp(r1 * t) + C2 * Math.exp(r2 * t);
          } else if (pi === 1) {
            // y = (C1 + C2·t)·e^(r1·t);  y(0)=2, y'(0)=0 → C1=2, C2=2·2=4
            yFn = t => (y0sol + y0sol * (-r1) * t) * Math.exp(r1 * t);
          } else {
            // y = e^(-γt)(A·cos(ωd·t)+B·sin(ωd·t)); y(0)=2, y'(0)=0
            const gam = gamma, wd = Math.sqrt(omega * omega - gam * gam);
            const A = y0sol, B = gam * y0sol / wd;
            yFn = t => Math.exp(-gam * t) * (A * Math.cos(wd * t) + B * Math.sin(wd * t));
          }

          // Second IC: y(0)=0, y'(0)=2
          let yFn2;
          if (pi === 0) {
            const C1b = 2 / (r1 - r2);
            const C2b = -C1b;
            yFn2 = t => C1b * Math.exp(r1 * t) + C2b * Math.exp(r2 * t);
          } else if (pi === 1) {
            yFn2 = t => 2 * t * Math.exp(r1 * t);
          } else {
            const gam = gamma, wd = Math.sqrt(omega * omega - gam * gam);
            yFn2 = t => Math.exp(-gam * t) * (2 / wd) * Math.sin(wd * t);
          }

          const nP = 100;
          const mkPoly = fn => {
            const segs = []; let seg = [];
            for (let i = 0; i <= nP; i++) {
              const t = tMin + (tMax - tMin) * i / nP;
              const y = fn(t);
              if (y >= yMin - 0.05 && y <= yMax + 0.05) {
                seg.push(`${toX(t).toFixed(1)},${toY(y).toFixed(1)}`);
              } else {
                if (seg.length > 1) segs.push(seg);
                seg = [];
              }
            }
            if (seg.length > 1) segs.push(seg);
            return segs;
          };

          const seg1 = mkPoly(yFn);
          const seg2 = mkPoly(yFn2);

          // Envelope for underdamped
          const envUp = pi === 2
            ? Array.from({ length: 60 }, (_, i) => {
                const t = tMin + (tMax - tMin) * i / 59;
                const e = cases[2].gamma;
                return `${toX(t).toFixed(1)},${toY(y0sol * Math.exp(-e * t)).toFixed(1)}`;
              }).join(' ')
            : null;
          const envDn = pi === 2
            ? Array.from({ length: 60 }, (_, i) => {
                const t = tMin + (tMax - tMin) * i / 59;
                const e = cases[2].gamma;
                return `${toX(t).toFixed(1)},${toY(-y0sol * Math.exp(-e * t)).toFixed(1)}`;
              }).join(' ')
            : null;

          return (
            <g key={pi}>
              {/* Panel */}
              <rect x={px} y={8} width={panW} height={H - 16} rx={5}
                fill={fill} stroke={color} strokeWidth="1.8" />
              <rect x={px + 4} y={12} width={panW - 8} height={36} rx={3} fill={color} />
              <text x={px + panW / 2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                {label}
              </text>
              <text x={px + panW / 2} y={41} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">
                {sub}
              </text>

              <clipPath id={`charClip${pi}`}>
                <rect x={aX} y={aY} width={aW} height={aH} />
              </clipPath>

              {/* Plot area */}
              <rect x={aX} y={aY} width={aW} height={aH} fill="#fff" stroke="#ddd" rx={2} />

              {/* Grid */}
              {[-1, 0, 1, 2].map(v => (
                <line key={v} x1={aX} y1={toY(v)} x2={aX + aW} y2={toY(v)}
                  stroke={v === 0 ? '#ccc' : '#f0f0f0'} strokeWidth={v === 0 ? 1.2 : 0.7} />
              ))}
              {[1, 2, 3, 4, 5].map(v => (
                <line key={v} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY + aH}
                  stroke="#f5f5f5" strokeWidth="0.7" />
              ))}

              {/* Axis */}
              <line x1={aX} y1={toY(0)} x2={aX + aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.2" />
              <line x1={aX} y1={aY} x2={aX} y2={aY + aH} stroke="#bbb" strokeWidth="1" />

              {/* Envelope (underdamped) */}
              {pi === 2 && (
                <>
                  <polyline points={envUp} fill="none" stroke={color}
                    strokeWidth="1.2" strokeDasharray="5,3" opacity="0.5"
                    clipPath={`url(#charClip${pi})`} />
                  <polyline points={envDn} fill="none" stroke={color}
                    strokeWidth="1.2" strokeDasharray="5,3" opacity="0.5"
                    clipPath={`url(#charClip${pi})`} />
                </>
              )}

              {/* Curves */}
              {seg2.map((pts, si) => (
                <polyline key={`s2-${si}`} points={pts.join(' ')} fill="none"
                  stroke={color} strokeWidth="1.6" strokeDasharray="6,3" opacity="0.55"
                  clipPath={`url(#charClip${pi})`} />
              ))}
              {seg1.map((pts, si) => (
                <polyline key={`s1-${si}`} points={pts.join(' ')} fill="none"
                  stroke={color} strokeWidth="2.5" clipPath={`url(#charClip${pi})`} />
              ))}

              {/* Axis ticks */}
              {[2, 4, 6].map(v => (
                <text key={v} x={toX(v)} y={toY(0) + 13} textAnchor="middle"
                  fontSize="8.5" fill="#aaa">{v}</text>
              ))}
              <text x={aX + aW + 5} y={toY(0) + 4} fontSize="9" fill="#888">t</text>
              <text x={aX - 4} y={toY(y0sol) + 4} textAnchor="end" fontSize="8.5" fill="#aaa">2</text>
              <text x={aX + 3} y={aY - 4} fontSize="9" fill="#888">y</text>

              {/* Info strip */}
              <rect x={px + 6} y={aY + aH + 10} width={panW - 12} height={H - 16 - aY - aH - 14}
                rx={3} fill="#fff" stroke={color} strokeWidth="0.8" />
              <text x={px + panW / 2} y={aY + aH + 24} textAnchor="middle"
                fontSize="9" fill={color} fontFamily="'Courier New',monospace" fontWeight="700">
                {rootLabel}
              </text>
              {desc.map((ln, di) => (
                <text key={di} x={px + panW / 2} y={aY + aH + 38 + di * 16}
                  textAnchor="middle" fontSize="9.5" fill={di === 0 ? color : '#555'}
                  fontWeight={di === 0 ? '700' : '400'}>{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The characteristic equation r² + 2γr + ω₀² = 0 has roots r = −γ ± √(γ²−ω₀²). When γ {">"} ω₀ (overdamped) both roots are real and negative — the system decays without oscillating. When γ = ω₀ (critically damped) a repeated root gives the fastest return to zero. When γ {"<"} ω₀ (underdamped) complex roots produce decaying oscillations; the dashed envelope e^(−γt) bounds the amplitude. Solid curves: y(0)=2, y′(0)=0. Dashed lighter curves: y(0)=0, y′(0)=2.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Phase Plane Portrait for y'' + 2γy' + ω₀²y = 0
//             Trajectories in (y, y') plane for all three cases
// ─────────────────────────────────────────────────────────────────
function DiagramPhasePlane() {
  const W = 900, H = 340;
  const panW = (W - 28) / 3;

  // Same three cases
  const cases = [
    { label: 'Overdamped', color: '#c0392b', fill: '#fff0f0',
      r1: -2 + Math.sqrt(3), r2: -2 - Math.sqrt(3), type: 'over' },
    { label: 'Critically Damped', color: '#e07b00', fill: '#fff7ee',
      r1: -2, r2: -2, type: 'crit' },
    { label: 'Underdamped', color: '#2E75B6', fill: '#EBF3FB',
      gamma: 0.4, omega: 2, type: 'under' },
  ];

  const aH = 175, aYoff = 48;
  const yMin = -2.5, yMax = 2.5, vMin = -3.5, vMax = 3.5;

  // Multiple ICs for each panel
  const icSets = [
    [{ y: 2, v: 0 }, { y: -2, v: 0 }, { y: 0, v: 2 }, { y: 1.5, v: -1 }],
    [{ y: 2, v: 0 }, { y: -2, v: 0 }, { y: 0, v: 2 }, { y: 1, v: 1 }],
    [{ y: 2, v: 0 }, { y: -2, v: 0 }, { y: 0, v: 2.5 }, { y: 1.5, v: 1.5 }],
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Phase Plane Portraits — Trajectories in (y, y′) Space for Each Damping Case
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {cases.map(({ label, color, fill, r1, r2, gamma, omega, type }, pi) => {
          const px  = 10 + pi * (panW + 4);
          const aX  = px + 36;
          const aY  = aYoff;
          const aW  = panW - 50;
          const toX = y => aX + (y - yMin) / (yMax - yMin) * aW;
          const toV = v => aY + aH - (v - vMin) / (vMax - vMin) * aH;
          const tMax2 = 12, nP = 200;

          const mkTraj = ({ y: y0, v: v0 }) => {
            // Integrate numerically: y'' = -2γy' - ω₀²y
            const gam = type === 'under' ? gamma : Math.abs(r1 + r2) / 2;
            const om2 = type === 'under' ? omega * omega : r1 * r2;
            const dt = tMax2 / nP;
            let y = y0, v = v0;
            const pts = [];
            for (let i = 0; i <= nP; i++) {
              if (y >= yMin - 0.1 && y <= yMax + 0.1 && v >= vMin - 0.1 && v <= vMax + 0.1) {
                pts.push(`${toX(y).toFixed(1)},${toV(v).toFixed(1)}`);
              } else if (pts.length > 1) break;
              const ay = -2 * gam * v - om2 * y;
              v += ay * dt;
              y += v * dt;
            }
            return pts;
          };

          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={H - 16} rx={5}
                fill={fill} stroke={color} strokeWidth="1.8" />
              <rect x={px + 4} y={12} width={panW - 8} height={32} rx={3} fill={color} />
              <text x={px + panW / 2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                {label}
              </text>
              <text x={px + panW / 2} y={40} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">
                Phase portrait  y vs y′
              </text>

              <clipPath id={`ppClip${pi}`}>
                <rect x={aX} y={aY} width={aW} height={aH} />
              </clipPath>

              <rect x={aX} y={aY} width={aW} height={aH} fill="#fff" stroke="#ddd" rx={2} />

              {/* Grid */}
              {[-2, -1, 0, 1, 2].map(v => (
                <g key={v}>
                  <line x1={aX} y1={toV(v)} x2={aX + aW} y2={toV(v)}
                    stroke={v === 0 ? '#ccc' : '#f5f5f5'} strokeWidth={v === 0 ? 1.2 : 0.7} />
                  <line x1={toX(v)} y1={aY} x2={toX(v)} y2={aY + aH}
                    stroke={v === 0 ? '#ccc' : '#f5f5f5'} strokeWidth={v === 0 ? 1.2 : 0.7} />
                </g>
              ))}

              {/* Axes labels */}
              <text x={aX + aW + 5} y={toV(0) + 4} fontSize="9" fill="#888">y</text>
              <text x={toX(0) + 3} y={aY - 4} fontSize="9" fill="#888">y′</text>

              {/* Trajectories */}
              {icSets[pi].map((ic, ii) => {
                const pts = mkTraj(ic);
                if (pts.length < 2) return null;
                return (
                  <g key={ii}>
                    <polyline points={pts.join(' ')} fill="none"
                      stroke={color} strokeWidth="1.8" opacity={0.6 + ii * 0.1}
                      clipPath={`url(#ppClip${pi})`} />
                    <circle cx={toX(ic.y)} cy={toV(ic.v)} r={3.5}
                      fill={color} stroke="#fff" strokeWidth="1"
                      clipPath={`url(#ppClip${pi})`} />
                  </g>
                );
              })}

              {/* Equilibrium at origin */}
              <circle cx={toX(0)} cy={toV(0)} r={5}
                fill={type === 'under' ? color : '#fff'}
                stroke={color} strokeWidth="2"
                clipPath={`url(#ppClip${pi})`} />

              {/* Axis ticks */}
              {[-2, 2].map(v => (
                <g key={v}>
                  <text x={toX(v)} y={toV(0) + 13} textAnchor="middle" fontSize="8" fill="#bbb">{v}</text>
                  <text x={aX - 4} y={toV(v) + 4} textAnchor="end" fontSize="8" fill="#bbb">{v}</text>
                </g>
              ))}

              {/* Bottom annotation */}
              <rect x={px + 6} y={aY + aH + 10} width={panW - 12} height={H - 16 - aY - aH - 14}
                rx={3} fill="#fff" stroke={color} strokeWidth="0.8" />
              <text x={px + panW / 2} y={aY + aH + 24} textAnchor="middle"
                fontSize="9.5" fill={color} fontWeight="700">
                {type === 'over' && 'Stable node'}
                {type === 'crit' && 'Stable star node'}
                {type === 'under' && 'Stable spiral'}
              </text>
              <text x={px + panW / 2} y={aY + aH + 40} textAnchor="middle" fontSize="9" fill="#555">
                {type === 'over' && 'All trajectories → origin'}
                {type === 'crit' && 'Tangent to eigenvector'}
                {type === 'under' && 'Spirals inward to origin'}
              </text>
              <text x={px + panW / 2} y={aY + aH + 56} textAnchor="middle" fontSize="9" fill="#555">
                {type === 'over' && 'along slow eigendir.'}
                {type === 'crit' && 'as t → ∞'}
                {type === 'under' && 'Frequency ωd = √(ω₀²−γ²)'}
              </text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The phase plane plots y′ vs y, eliminating time. Dots mark initial conditions; arrows indicate the direction of increasing t. Overdamped: trajectories glide into the origin along the slow eigendirection. Critically damped: all paths tangent to the single eigendirection. Underdamped: inward spirals whose angular speed equals the damped frequency ωd = √(ω₀²−γ²). The equilibrium at the origin is stable in all three cases since Re(r) {"<"} 0.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Method of Undetermined Coefficients
//             Particular solution for y'' + py' + qy = g(x)
// ─────────────────────────────────────────────────────────────────
function DiagramUndeterminedCoefficients() {
  const W = 900, H = 340;

  // Example: y'' - y' - 2y = 4x·e^x
  // Homogeneous: r²-r-2=0 → (r-2)(r+1)=0 → r=2,-1
  // Particular guess: yp = (Ax+B)e^x
  // yp' = (A + (Ax+B))e^x = (Ax+A+B)e^x
  // yp'' = (A + (Ax+A+B))e^x = (Ax+2A+B)e^x
  // Sub: (Ax+2A+B) - (Ax+A+B) - 2(Ax+B) = 4x
  // = Ax+2A+B - Ax-A-B - 2Ax-2B = -2Ax+(A-2B) = 4x
  // So -2A=4 → A=-2; A-2B=0 → B=-1
  // yp = (-2x-1)e^x
  // General: y = C1·e^(2x) + C2·e^(-x) + (-2x-1)e^x

  const aX=54, aY=32, aW=430, aH=240;
  const xMin=-0.5, xMax=2.5, yMin=-5, yMax=12;
  const toX=v=>aX+(v-xMin)/(xMax-xMin)*aW;
  const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;

  const yp  = x => (-2*x-1)*Math.exp(x);
  const yh1 = (C1,C2) => x => C1*Math.exp(2*x)+C2*Math.exp(-x);
  const yGen= (C1,C2) => x => C1*Math.exp(2*x)+C2*Math.exp(-x)+yp(x);

  // Several particular+homogeneous combinations
  const solutions=[
    {C1:0.3,C2:0.5,color:'#2E75B6'},
    {C1:0,  C2:1,  color:'#27ae60'},
    {C1:-0.2,C2:0.2,color:'#9b59b6'},
    {C1:0.1, C2:-0.5,color:'#e07b00'},
  ];

  const nP=100;
  const mkCurve=(fn,col,width=2,dash='')=>{
    const segs=[]; let seg=[];
    for(let i=0;i<=nP;i++){
      const x=xMin+(xMax-xMin)*i/nP;
      const y=fn(x);
      if(isFinite(y)&&y>=yMin-0.2&&y<=yMax+0.2){
        seg.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
      } else { if(seg.length>1)segs.push(seg); seg=[]; }
    }
    if(seg.length>1)segs.push(seg);
    return segs.map((pts,si)=>(
      <polyline key={si} points={pts.join(' ')} fill="none"
        stroke={col} strokeWidth={width} strokeDasharray={dash}
        clipPath="url(#ucClip)"/>
    ));
  };

  const panX=520, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Method of Undetermined Coefficients — y″ − y′ − 2y = 4xe^x · yp = (−2x−1)e^x
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={500} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={492} height={22} rx={3} fill="#1F3864"/>
        <text x={264} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          General solution = yₕ + yₚ  (blue dashed = particular solution yₚ alone)
        </text>

        <clipPath id="ucClip"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {/* Grid */}
        {[-4,-2,0,2,4,6,8,10].filter(v=>v>=yMin&&v<=yMax).map(v=>(
          <line key={v} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)}
            stroke={v===0?'#ccc':'#f0f0f0'} strokeWidth={v===0?1.2:0.7}/>
        ))}
        {[-0.5,0,0.5,1,1.5,2,2.5].map(v=>(
          <line key={v} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH}
            stroke={v===0?'#ccc':'#f5f5f5'} strokeWidth={v===0?1.2:0.7}/>
        ))}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.3"/>
        <line x1={toX(0)} y1={aY} x2={toX(0)} y2={aY+aH} stroke="#bbb" strokeWidth="1.3"/>

        {/* General solutions */}
        {solutions.map(({C1,C2,color})=>mkCurve(yGen(C1,C2),color,2))}

        {/* Particular solution yp */}
        {mkCurve(yp,'#c0392b',2.8,'8,4')}

        {/* Axis labels */}
        {[-0.5,0.5,1,1.5,2,2.5].map(v=>(
          <text key={v} x={toX(v)} y={toY(0)+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
        ))}
        {[-4,-2,2,4,6,8,10].filter(v=>v>=yMin&&v<=yMax).map(v=>(
          <text key={v} x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
        ))}
        <text x={aX+aW+6} y={toY(0)+4} fontSize="10" fill="#555">x</text>
        <text x={toX(0)+4} y={aY-6} fontSize="10" fill="#555">y</text>

        {/* Legend */}
        <line x1={aX+4} y1={aY+aH+18} x2={aX+22} y2={aY+aH+18} stroke="#c0392b" strokeWidth="2.5" strokeDasharray="7,3"/>
        <text x={aX+26} y={aY+aH+22} fontSize="9.5" fill="#c0392b">yₚ = (−2x−1)eˣ (particular)</text>
        <line x1={aX+230} y1={aY+aH+18} x2={aX+248} y2={aY+aH+18} stroke="#2E75B6" strokeWidth="2"/>
        <text x={aX+252} y={aY+aH+22} fontSize="9.5" fill="#444">yₕ+yₚ (general, various C)</text>

        {/* RIGHT PANEL */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Undetermined Coefficients
        </text>
        {[
          {y:32, bg:'#EBF3FB',bc:'#2E75B6',title:'When to use',
           lines:['g(x) is polynomial,','exponential, sin/cos,','or products of these','(constant-coef. ODE)']},
          {y:120,bg:'#f0fff4',bc:'#27ae60',title:'Guess table',
           lines:['g=Pₙ(x): yₚ=Qₙ(x)','g=e^αx: yₚ=Ae^αx','g=sin/cos(βx): A·cos+B·sin','g=e^αx·Pₙ: combine above']},
          {y:208,bg:'#fff7ee',bc:'#e07b00',title:'Modification rule',
           lines:['If guess ∈ yₕ, multiply','by x (or x² if double)','to get independent','particular solution']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={88} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The method of undetermined coefficients applies when the right-hand side g(x) belongs to a limited class of functions (polynomials, exponentials, sinusoids, and their products). We guess the form of yₚ with unknown coefficients, substitute into the ODE, and match terms. The general solution is always y = yₕ + yₚ. The dashed red curve is yₚ alone; coloured curves add different homogeneous components C₁e^(2x) + C₂e^(−x).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Variation of Parameters: Wronskian and quadrature
// ─────────────────────────────────────────────────────────────────
function DiagramVariationOfParameters() {
  const W = 900, H = 340;

  // Example: y'' + y = sec(x)  (can't use undetermined coefficients)
  // yh = C1·cos(x) + C2·sin(x)
  // W = cos²x + sin²x = 1
  // u1' = -y2·g/W = -sin(x)·sec(x) = -tan(x)  → u1 = ln|cos(x)|
  // u2' = y1·g/W = cos(x)·sec(x) = 1         → u2 = x
  // yp = cos(x)·ln|cos(x)| + x·sin(x)

  const aX=54, aY=32, aW=430, aH=240;
  const xMin=0, xMax=1.4, yMin=-1, yMax=4;
  const toX=v=>aX+(v-xMin)/(xMax-xMin)*aW;
  const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;

  const y1  = x=>Math.cos(x);
  const y2  = x=>Math.sin(x);
  const yp  = x=>Math.cos(x)*Math.log(Math.abs(Math.cos(x)))+x*Math.sin(x);
  const yGen= (C1,C2) => x => C1*Math.cos(x)+C2*Math.sin(x)+yp(x);

  const nP=100;
  const mkSVGCurve=(fn,col,w=2,dash='')=>{
    const segs=[]; let seg=[];
    for(let i=0;i<=nP;i++){
      const x=xMin+(xMax-xMin)*i/nP;
      const y=fn(x);
      if(isFinite(y)&&y>=yMin-0.1&&y<=yMax+0.1){
        seg.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
      } else { if(seg.length>1)segs.push(seg); seg=[]; }
    }
    if(seg.length>1)segs.push(seg);
    return segs.map((pts,si)=>(
      <polyline key={si} points={pts.join(' ')} fill="none"
        stroke={col} strokeWidth={w} strokeDasharray={dash}
        clipPath="url(#vpClip)"/>
    ));
  };

  const panX=520, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Variation of Parameters — y″ + y = sec(x) · yₚ = cos(x)ln|cos(x)| + x·sin(x)
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={500} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={492} height={22} rx={3} fill="#9b59b6"/>
        <text x={264} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Homogeneous basis y₁=cos(x), y₂=sin(x) and particular solution yₚ (dashed)
        </text>

        <clipPath id="vpClip"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {/* Grid */}
        {[-1,0,1,2,3,4].filter(v=>v>=yMin&&v<=yMax).map(v=>(
          <line key={v} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)}
            stroke={v===0?'#ccc':'#f0f0f0'} strokeWidth={v===0?1.2:0.7}/>
        ))}
        {[0,0.5,1,1.4].map(v=>(
          <line key={v} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH}
            stroke="#f5f5f5" strokeWidth="0.7"/>
        ))}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.3"/>
        <line x1={aX} y1={aY} x2={aX} y2={aY+aH} stroke="#bbb" strokeWidth="1.3"/>

        {/* Basis functions */}
        {mkSVGCurve(y1,'#27ae60',2,'6,3')}
        {mkSVGCurve(y2,'#e07b00',2,'6,3')}
        {/* Particular */}
        {mkSVGCurve(yp,'#9b59b6',2.5,'9,4')}
        {/* General solutions */}
        {[{C1:1,C2:0.5},{C1:0.5,C2:1},{C1:0,C2:1.5}].map(({C1,C2},i)=>(
          mkSVGCurve(yGen(C1,C2),'#c0392b',1.8+(i===1?0.6:0))
        ))}

        {/* Axis ticks */}
        {[0,0.5,1,1.4].map(v=>(
          <text key={v} x={toX(v)} y={toY(0)+14} textAnchor="middle" fontSize="9" fill="#888">{v.toFixed(1)}</text>
        ))}
        {[-1,0,1,2,3,4].filter(v=>v>=yMin&&v<=yMax).map(v=>(
          <text key={v} x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
        ))}
        <text x={aX+aW+6} y={toY(0)+4} fontSize="10" fill="#555">x</text>
        <text x={aX+4} y={aY-6} fontSize="10" fill="#555">y</text>

        {/* Legend */}
        {[
          {col:'#27ae60',dash:'5,3',lbl:'y₁=cos(x)'},
          {col:'#e07b00',dash:'5,3',lbl:'y₂=sin(x)'},
          {col:'#9b59b6',dash:'8,4',lbl:'yₚ (particular)'},
          {col:'#c0392b',dash:'',   lbl:'yₕ+yₚ (general)'},
        ].map(({col,dash,lbl},li)=>(
          <g key={li}>
            <line x1={aX+4+li*116} y1={aY+aH+18} x2={aX+22+li*116} y2={aY+aH+18}
              stroke={col} strokeWidth="2" strokeDasharray={dash}/>
            <text x={aX+26+li*116} y={aY+aH+22} fontSize="9" fill={col}>{lbl}</text>
          </g>
        ))}

        {/* RIGHT PANEL */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Variation of Parameters
        </text>
        {[
          {y:32, bg:'#f5d0ff',bc:'#9b59b6',title:'Setup',
           lines:['yₚ = u₁(x)y₁ + u₂(x)y₂','Assume u₁′y₁+u₂′y₂=0','and u₁′y₁′+u₂′y₂′=g']},
          {y:118,bg:'#EBF3FB',bc:'#2E75B6',title:'Wronskian W',
           lines:['W=y₁y₂′ − y₂y₁′','u₁′ = −y₂g/W','u₂′ =  y₁g/W']},
          {y:204,bg:'#f0fff4',bc:'#27ae60',title:'Advantages',
           lines:['Works for any g(x)','Continuous on interval','No guessing required','More general than UC']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={80} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}

        {/* Wronskian formula box */}
        <rect x={panX+8} y={292} width={panW-16} height={32} rx={4} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={panX+panW/2} y={306} textAnchor="middle" fontSize="10.5"
          fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          W[cos,sin]=cos²x+sin²x=1
        </text>
        <text x={panX+panW/2} y={320} textAnchor="middle" fontSize="9.5" fill="#555">
          u₁=ln|cos x|, u₂=x ✓
        </text>
      </svg>
      <div style={S.dCaption}>
        Variation of parameters works for any continuous g(x), unlike undetermined coefficients which requires special forms. The key is replacing constants C₁,C₂ with functions u₁(x),u₂(x) and imposing the constraint u₁′y₁+u₂′y₂=0. This yields a 2×2 system solved via the Wronskian W=y₁y₂′−y₂y₁′, then u₁,u₂ found by integration. The method always works as long as the homogeneous solution basis is known.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Forced Oscillations and Resonance
// ─────────────────────────────────────────────────────────────────
function DiagramResonance() {
  const W = 900, H = 340;

  // Forced ODE: y'' + 2γy' + ω₀²y = F₀cos(ωt)
  // Steady-state amplitude A(ω) = F₀ / √((ω₀²-ω²)² + (2γω)²)
  const F0=1, omega0=3;

  const A = (om, gam) => F0 / Math.sqrt((omega0**2-om**2)**2 + (2*gam*om)**2);

  // LEFT: amplitude response vs ω for several γ values
  const aX=54, aY=28, aW=390, aH=230;
  const omMin=0, omMax=7, Amax=3.5;
  const toX=v=>aX+(v-omMin)/(omMax-omMin)*aW;
  const toA=a=>aY+aH-Math.min(a,Amax)/Amax*aH;

  const gamCases=[
    {gam:0.15,color:'#c0392b',label:'γ=0.15'},
    {gam:0.5, color:'#e07b00',label:'γ=0.5'},
    {gam:1.0, color:'#27ae60',label:'γ=1.0'},
    {gam:2.0, color:'#2E75B6',label:'γ=2.0'},
  ];

  // RIGHT: time-domain resonance illustration  γ≈0, ω≈ω₀
  const rpX=490, rpW=W-rpX-14;
  const bX=rpX+44, bY=28, bW=rpW-58, bH=230;
  const tMin=0,tMax=20;
  const toTX=v=>bX+(v-tMin)/(tMax-tMin)*bW;
  const toTY=v=>bY+bH/2-v*(bH*0.4/3);

  // Near-resonance: y(t) ≈ (F0/2γω0)·sin(ω0t) growing envelope
  const resonPts=Array.from({length:300},(_,i)=>{
    const t=tMin+(tMax-tMin)*i/299;
    // Exact solution at resonance (γ→0, ω=ω0): y = (F0/(2ω0))·t·sin(ω0·t)
    const y=(F0/(2*omega0))*t*Math.sin(omega0*t);
    const yc=Math.max(-3,Math.min(3,y));
    return `${toTX(t).toFixed(1)},${toTY(yc).toFixed(1)}`;
  }).join(' ');

  // Envelope
  const envU=Array.from({length:60},(_,i)=>{
    const t=tMin+(tMax-tMin)*i/59;
    const e=(F0/(2*omega0))*t;
    return `${toTX(t).toFixed(1)},${toTY(Math.min(e,3)).toFixed(1)}`;
  }).join(' ');
  const envD=Array.from({length:60},(_,i)=>{
    const t=tMin+(tMax-tMin)*i/59;
    const e=(F0/(2*omega0))*t;
    return `${toTX(t).toFixed(1)},${toTY(-Math.min(e,3)).toFixed(1)}`;
  }).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Forced Oscillations — Amplitude Response A(ω) and Resonance (ω → ω₀, γ → 0)
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: frequency response */}
        <rect x={10} y={8} width={470} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={462} height={22} rx={3} fill="#1F3864"/>
        <text x={254} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Amplitude A(ω)=F₀/√((ω₀²−ω²)²+(2γω)²)  ·  ω₀=3
        </text>

        <clipPath id="resClip"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {/* Grid */}
        {[0.5,1,1.5,2,2.5,3].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toA(v)} x2={aX+aW} y2={toA(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
            <text x={aX-5} y={toA(v)+4} textAnchor="end" fontSize="8.5" fill="#bbb">{v}</text>
          </g>
        ))}
        {[0,1,2,3,4,5,6,7].map(v=>(
          <line key={v} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f5f5f5" strokeWidth="0.7"/>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.4"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1.2"/>

        {/* ω₀ reference line */}
        <line x1={toX(omega0)} y1={aY} x2={toX(omega0)} y2={aY+aH}
          stroke="#1F3864" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.5"/>
        <text x={toX(omega0)+3} y={aY+14} fontSize="9.5" fill="#1F3864" fontWeight="700">ω₀=3</text>

        {/* Response curves */}
        {gamCases.map(({gam,color})=>{
          const pts=Array.from({length:200},(_,i)=>{
            const om=omMin+(omMax-omMin)*i/199;
            const a=A(om,gam);
            const ac=Math.min(a,Amax+0.1);
            return `${toX(om).toFixed(1)},${toA(ac).toFixed(1)}`;
          }).join(' ');
          return <polyline key={gam} points={pts} fill="none" stroke={color}
            strokeWidth="2.2" clipPath="url(#resClip)"/>;
        })}

        {/* Peak markers */}
        {gamCases.map(({gam,color})=>{
          // Peak ω = √(ω₀²−2γ²) for damped resonance
          const omPeak=Math.sqrt(Math.max(0,omega0**2-2*gam**2));
          if(omPeak<=0||omPeak>omMax) return null;
          const aPeak=A(omPeak,gam);
          if(aPeak>Amax) return null;
          return (
            <circle key={gam} cx={toX(omPeak)} cy={toA(aPeak)} r={4}
              fill={color} stroke="#fff" strokeWidth="1" clipPath="url(#resClip)"/>
          );
        })}

        {/* Axis ticks */}
        {[0,1,2,3,4,5,6,7].map(v=>(
          <text key={v} x={toX(v)} y={aY+aH+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
        ))}
        <text x={aX+aW/2} y={aY+aH+28} textAnchor="middle" fontSize="10" fill="#555">Driving frequency ω</text>
        <text x={aX-38} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-38},${aY+aH/2})`}>Amplitude A(ω)</text>

        {/* Legend */}
        {gamCases.map(({color,label},li)=>(
          <g key={li}>
            <line x1={aX+4+li*100} y1={aY+aH+40} x2={aX+20+li*100} y2={aY+aH+40} stroke={color} strokeWidth="2.2"/>
            <text x={aX+24+li*100} y={aY+aH+44} fontSize="9.5" fill={color}>{label}</text>
          </g>
        ))}

        {/* RIGHT: time domain */}
        <rect x={rpX} y={8} width={rpW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={rpX+4} y={12} width={rpW-8} height={22} rx={3} fill="#c0392b"/>
        <text x={rpX+rpW/2} y={27} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
          Pure resonance: ω=ω₀, γ=0
        </text>

        <clipPath id="resTimeClip"><rect x={bX} y={bY} width={bW} height={bH}/></clipPath>

        {/* Axes */}
        <line x1={bX} y1={bY+bH/2} x2={bX+bW} y2={bY+bH/2} stroke="#aaa" strokeWidth="1.3"/>
        <line x1={bX} y1={bY}      x2={bX}    y2={bY+bH}    stroke="#aaa" strokeWidth="1.2"/>

        {/* Envelope */}
        <polyline points={envU} fill="none" stroke="#c0392b" strokeWidth="1.5"
          strokeDasharray="6,3" clipPath="url(#resTimeClip)"/>
        <polyline points={envD} fill="none" stroke="#c0392b" strokeWidth="1.5"
          strokeDasharray="6,3" clipPath="url(#resTimeClip)"/>

        {/* Resonance solution */}
        <polyline points={resonPts} fill="none" stroke="#c0392b" strokeWidth="2.2"
          clipPath="url(#resTimeClip)"/>

        {/* Labels */}
        <text x={bX+bW-2} y={bY+bH/2-toTY(0)+toTY(0)} fontSize="9" fill="#888" textAnchor="end">t</text>
        <text x={bX+4} y={bY-4} fontSize="9" fill="#888">y</text>
        <text x={bX+bW/2} y={bY+bH+16} textAnchor="middle" fontSize="9.5" fill="#c0392b" fontWeight="700">
          y ≈ (t/2ω₀)sin(ω₀t) — amplitude grows linearly
        </text>

        {[5,10,15,20].map(v=>(
          <text key={v} x={toTX(v)} y={bY+bH/2+14} textAnchor="middle" fontSize="8.5" fill="#aaa">{v}</text>
        ))}
      </svg>
      <div style={S.dCaption}>
        The amplitude response curve peaks near ω = ω₀ (the natural frequency); with light damping γ the peak can be very tall. As γ→0 and ω→ω₀ simultaneously (pure resonance), the particular solution grows without bound: y ∝ t·sin(ω₀t). The dashed envelope lines show the linearly growing amplitude. In practice, even small damping prevents infinite growth, but the response can still be destructively large near resonance.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Power Series Method: solution near ordinary point
// ─────────────────────────────────────────────────────────────────
function DiagramPowerSeries() {
  const W = 900, H = 340;

  // ODE: y'' + xy' + y = 0  (ordinary point at x=0)
  // Series solution: y = a₀Σ(-1)^n x^(2n)/(2n)!! + a₁Σ (odd terms)
  // Simpler: Airy-like. Use truncated power series vs exact (Hermite-like).
  // Actually use: y'' - xy = 0 (Airy equation)  
  // But to keep it accessible, use y'' + y = 0 and show how series gives cos/sin.
  // Then contrast with one that NEEDS power series: (1-x²)y'' - 2xy' + 2y = 0 (Legendre n=1)
  // Exact: y = x.  Series: a0(1 - x² - x⁴/3 - ...) + a1·x

  // Show the power series truncations converging to a known exact solution
  // y'' + y = 0, y(0)=1, y'(0)=0  → exact cos(x)
  // Power series: y = 1 - x²/2! + x⁴/4! - x⁶/6! + ...

  const exact = x => Math.cos(x);
  const psTrunc = (x, terms) => {
    let s=0, sign=1, fact=1;
    for(let n=0;n<terms;n++){
      fact = n===0?1:fact*(2*n-1)*(2*n);
      s+=sign*Math.pow(x,2*n)/fact;
      sign*=-1;
    }
    return s;
  };

  const aX=54, aY=28, aW=400, aH=240;
  const xMin=0, xMax=8, yMin=-2, yMax=2;
  const toX=v=>aX+(v-xMin)/(xMax-xMin)*aW;
  const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;

  const termCases=[
    {terms:1, color:'#c0392b', label:'1 term: y=1'},
    {terms:2, color:'#e07b00', label:'2 terms: y=1−x²/2'},
    {terms:3, color:'#9b59b6', label:'3 terms'},
    {terms:4, color:'#27ae60', label:'4 terms'},
    {terms:6, color:'#2E75B6', label:'6 terms'},
  ];

  const nP=150;
  const mkC=(fn,col,w,dash='')=>{
    const segs=[]; let seg=[];
    for(let i=0;i<=nP;i++){
      const x=xMin+(xMax-xMin)*i/nP;
      const y=fn(x);
      if(isFinite(y)&&y>=yMin-0.1&&y<=yMax+0.1){
        seg.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
      } else { if(seg.length>1)segs.push(seg); seg=[]; }
    }
    if(seg.length>1)segs.push(seg);
    return segs.map((pts,si)=>(
      <polyline key={si} points={pts.join(' ')} fill="none"
        stroke={col} strokeWidth={w} strokeDasharray={dash} clipPath="url(#psClip)"/>
    ));
  };

  const panX=500, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Power Series Method — y″ + y = 0 · Truncated Series Converging to cos(x)
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={480} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={472} height={22} rx={3} fill="#1F3864"/>
        <text x={254} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Partial sums y=Σ(−1)ⁿx^(2n)/(2n)! converge to exact cos(x) (black)
        </text>

        <clipPath id="psClip"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)}
              stroke={v===0?'#ccc':'#f0f0f0'} strokeWidth={v===0?1.2:0.7}/>
            <text x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="8.5" fill="#bbb">{v}</text>
          </g>
        ))}
        {[0,1,2,3,4,5,6,7,8].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f5f5f5" strokeWidth="0.7"/>
            <text x={toX(v)} y={aY+aH+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.3"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#bbb" strokeWidth="1.2"/>

        {/* Convergence radii (π/2 multiples) */}
        {[Math.PI,2*Math.PI].map((v,vi)=>(
          v<xMax&&(
            <line key={vi} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH}
              stroke="#bbb" strokeWidth="0.8" strokeDasharray="3,3"/>
          )
        ))}

        {/* Truncated series */}
        {termCases.map(({terms,color})=>mkC(x=>psTrunc(x,terms),color,1.8))}

        {/* Exact cos(x) */}
        {mkC(exact,'#1F3864',2.8,'8,0')}

        {/* Legend */}
        <line x1={aX+4} y1={aY+aH+26} x2={aX+22} y2={aY+aH+26} stroke="#1F3864" strokeWidth="2.8"/>
        <text x={aX+26} y={aY+aH+30} fontSize="9.5" fill="#1F3864" fontWeight="700">Exact cos(x)</text>
        {termCases.map(({color,label},li)=>(
          <g key={li}>
            <line x1={aX+130+li*74} y1={aY+aH+26} x2={aX+148+li*74} y2={aY+aH+26}
              stroke={color} strokeWidth="1.8"/>
            <text x={aX+152+li*74} y={aY+aH+30} fontSize="8.5" fill={color}>{label.split(':')[0]}</text>
          </g>
        ))}

        <text x={aX+aW+5} y={toY(0)+4} fontSize="10" fill="#555">x</text>
        <text x={aX+4} y={aY-6} fontSize="10" fill="#555">y</text>

        {/* RIGHT PANEL */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Power Series Method
        </text>
        {[
          {y:32, bg:'#EBF3FB',bc:'#2E75B6',title:'Assume solution',
           lines:['y = Σ aₙxⁿ (n=0 to ∞)','y′=Σ naₙxⁿ⁻¹','y″=Σ n(n−1)aₙxⁿ⁻²']},
          {y:118,bg:'#f0fff4',bc:'#27ae60',title:'Recurrence relation',
           lines:['Sub into ODE; collect','like powers of x','Set each coef to 0','→ aₙ₊₂ in terms of aₙ']},
          {y:204,bg:'#fff7ee',bc:'#e07b00',title:'y″+y=0 result',
           lines:['a₂=-a₀/2!, a₄=a₀/4!','a₃=-a₁/3!, a₅=a₁/5!','y=a₀cos(x)+a₁sin(x)','Radius of conv.: ∞']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={82} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The power series method assumes y = Σaₙxⁿ, substitutes into the ODE, and equates coefficients of like powers to zero — yielding a recurrence for the aₙ. With just 1 term the approximation is flat (y=1); each additional pair of terms adds another oscillation, converging exponentially to cos(x). The truncated series is excellent near x=0 but diverges far away; for y″+y=0 the radius of convergence is infinite since both solutions cos and sin are entire.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Reduction of Order and Euler–Cauchy Equations
// ─────────────────────────────────────────────────────────────────
function DiagramReductionEuler() {
  const W = 900, H = 340;

  // LEFT half: Euler-Cauchy  x²y'' + xy' + y = 0  (constant coeff in ln x)
  // Try y=x^m: m²=0 → y1=cos(lnx), y2=sin(lnx) (complex roots m=±i)
  // RIGHT half: Reduction of order demo
  // y'' - (2/x)y' + (2/x²)y = 0, y₁=x known → find y₂=x²

  const panW=(W-28)/2;

  // LEFT: Euler-Cauchy solutions
  const aX_L=50, aY_L=48, aW_L=panW-66, aH_L=195;
  const xMin_L=0.1, xMax_L=Math.exp(3*Math.PI), yMin_L=-1.5, yMax_L=1.5;
  const toX_L=v=>10+aX_L+(Math.log(v)-Math.log(xMin_L))/(Math.log(xMax_L)-Math.log(xMin_L))*aW_L;
  const toY_L=v=>aY_L+aH_L-(v-yMin_L)/(yMax_L-yMin_L)*aH_L;

  const y1_EC=x=>Math.cos(Math.log(x));
  const y2_EC=x=>Math.sin(Math.log(x));

  const nP=200;
  const mkEC=(fn,col,w=2,dash='')=>{
    const pts=Array.from({length:nP},(_,i)=>{
      const x=xMin_L+(xMax_L-xMin_L)*i/(nP-1);
      const y=fn(x);
      if(isFinite(y)&&y>=yMin_L-0.1&&y<=yMax_L+0.1)
        return `${toX_L(x).toFixed(1)},${toY_L(y).toFixed(1)}`;
      return null;
    }).filter(Boolean).join(' ');
    return pts.length>10?(
      <polyline points={pts} fill="none" stroke={col}
        strokeWidth={w} strokeDasharray={dash} clipPath="url(#ecClip)"/>
    ):null;
  };

  // RIGHT: reduction of order
  // y1=x², using v(x) to find y2
  // y'' - (2/x)y' + (2/x²)y = 0  →  y1=x, try y=vx
  // v''x + 2v' - (2/x)(v'x+v) + (2/x²)vx = v''x = 0 → v''=0 → v=Ax+B → y2=x·(Ax+B)=Ax²+Bx
  // Take y2=x² (independent from y1=x)
  const aX_R=10+panW+50, aY_R=48, aW_R=panW-66, aH_R=195;
  const xMin_R=0, xMax_R=2.5, yMin_R=-0.5, yMax_R=6;
  const toX_R=v=>aX_R+(v-xMin_R)/(xMax_R-xMin_R)*aW_R;
  const toY_R=v=>aY_R+aH_R-(v-yMin_R)/(yMax_R-yMin_R)*aH_R;

  const y1_R=x=>x;
  const y2_R=x=>x*x;
  const yGen_R=(C1,C2)=>x=>C1*x+C2*x*x;

  const mkR=(fn,col,w=2,dash='')=>{
    const segs=[]; let seg=[];
    for(let i=0;i<=100;i++){
      const x=xMin_R+(xMax_R-xMin_R)*i/100;
      const y=fn(x);
      if(isFinite(y)&&y>=yMin_R-0.1&&y<=yMax_R+0.1){
        seg.push(`${toX_R(x).toFixed(1)},${toY_R(y).toFixed(1)}`);
      } else { if(seg.length>1)segs.push(seg); seg=[]; }
    }
    if(seg.length>1)segs.push(seg);
    return segs.map((pts,si)=>(
      <polyline key={si} points={pts.join(' ')} fill="none"
        stroke={col} strokeWidth={w} strokeDasharray={dash}
        clipPath="url(#roClip)"/>
    ));
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>
        Euler–Cauchy Equation (left) and Reduction of Order (right)
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: Euler-Cauchy ── */}
        <rect x={10} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={panW-8} height={32} rx={3} fill="#9b59b6"/>
        <text x={14+panW/2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Euler–Cauchy: x²y″ + xy′ + y = 0
        </text>
        <text x={14+panW/2} y={40} textAnchor="middle" fontSize="10" fill="#ffffffaa">
          y = x^m, m=±i → y₁=cos(ln x), y₂=sin(ln x)
        </text>

        <clipPath id="ecClip">
          <rect x={10+aX_L} y={aY_L} width={aW_L} height={aH_L}/>
        </clipPath>
        <rect x={10+aX_L} y={aY_L} width={aW_L} height={aH_L} fill="#fafcff" stroke="#e0e0e0" rx={2}/>

        {/* Zero line */}
        <line x1={10+aX_L} y1={toY_L(0)} x2={10+aX_L+aW_L} y2={toY_L(0)}
          stroke="#ccc" strokeWidth="1.2"/>
        {/* Curves */}
        {mkEC(y1_EC,'#9b59b6',2.5)}
        {mkEC(y2_EC,'#c0392b',2.2,'5,3')}

        {/* X axis: log scale ticks */}
        {[1,2,5,10,50].map(v=>{
          const px=toX_L(v);
          if(px<10+aX_L||px>10+aX_L+aW_L) return null;
          return (
            <g key={v}>
              <line x1={px} y1={aY_L+aH_L} x2={px} y2={aY_L+aH_L+5} stroke="#aaa" strokeWidth="1"/>
              <text x={px} y={aY_L+aH_L+15} textAnchor="middle" fontSize="8.5" fill="#888">{v}</text>
            </g>
          );
        })}
        {[-1,0,1].map(v=>(
          <text key={v} x={10+aX_L-5} y={toY_L(v)+4} textAnchor="end" fontSize="8.5" fill="#bbb">{v}</text>
        ))}
        <text x={10+aX_L+aW_L+5} y={aY_L+aH_L+4} fontSize="9" fill="#888">x</text>
        <text x={10+aX_L+4} y={aY_L-4} fontSize="9" fill="#888">y</text>
        <text x={10+aX_L+aW_L/2} y={aY_L+aH_L+28} textAnchor="middle" fontSize="9" fill="#555">
          (x-axis on log scale)
        </text>

        {/* Legend */}
        <line x1={10+aX_L+4} y1={aY_L+aH_L+44} x2={10+aX_L+20} y2={aY_L+aH_L+44} stroke="#9b59b6" strokeWidth="2.5"/>
        <text x={10+aX_L+24} y={aY_L+aH_L+48} fontSize="9.5" fill="#9b59b6">y₁=cos(ln x)</text>
        <line x1={10+aX_L+134} y1={aY_L+aH_L+44} x2={10+aX_L+150} y2={aY_L+aH_L+44} stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3"/>
        <text x={10+aX_L+154} y={aY_L+aH_L+48} fontSize="9.5" fill="#c0392b">y₂=sin(ln x)</text>

        {/* ── RIGHT: Reduction of order ── */}
        <rect x={10+panW+8} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={10+panW+12} y={12} width={panW-8} height={32} rx={3} fill="#27ae60"/>
        <text x={10+panW+8+panW/2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Reduction of Order: xy″−2y′+(2/x)y=0
        </text>
        <text x={10+panW+8+panW/2} y={40} textAnchor="middle" fontSize="10" fill="#ffffffaa">
          Known y₁=x  →  find y₂=x² via y=v(x)·y₁
        </text>

        <clipPath id="roClip">
          <rect x={aX_R} y={aY_R} width={aW_R} height={aH_R}/>
        </clipPath>
        <rect x={aX_R} y={aY_R} width={aW_R} height={aH_R} fill="#fafcff" stroke="#e0e0e0" rx={2}/>

        {/* Grid */}
        {[1,2,3,4,5,6].filter(v=>v<=yMax_R).map(v=>(
          <line key={v} x1={aX_R} y1={toY_R(v)} x2={aX_R+aW_R} y2={toY_R(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
        ))}
        <line x1={aX_R} y1={toY_R(0)} x2={aX_R+aW_R} y2={toY_R(0)} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={aX_R} y1={aY_R} x2={aX_R} y2={aY_R+aH_R} stroke="#ccc" strokeWidth="1.2"/>

        {/* Basis functions */}
        {mkR(y1_R,'#27ae60',2.5)}
        {mkR(y2_R,'#2E75B6',2.2,'5,3')}
        {/* General solutions */}
        {[{C1:1,C2:0.5},{C1:-0.5,C2:1},{C1:0.5,C2:0.8}].map(({C1,C2},i)=>(
          mkR(yGen_R(C1,C2),'#e07b00',1.5+(i===0?0.5:0))
        ))}

        {/* Axis ticks */}
        {[0.5,1,1.5,2,2.5].map(v=>(
          <text key={v} x={toX_R(v)} y={toY_R(0)+14} textAnchor="middle" fontSize="8.5" fill="#888">{v}</text>
        ))}
        {[0,1,2,3,4,5,6].filter(v=>v<=yMax_R).map(v=>(
          <text key={v} x={aX_R-5} y={toY_R(v)+4} textAnchor="end" fontSize="8.5" fill="#aaa">{v}</text>
        ))}
        <text x={aX_R+aW_R+5} y={toY_R(0)+4} fontSize="9" fill="#888">x</text>
        <text x={aX_R+4} y={aY_R-4} fontSize="9" fill="#888">y</text>

        {/* Legend */}
        <line x1={aX_R+4} y1={aY_R+aH_R+18} x2={aX_R+20} y2={aY_R+aH_R+18} stroke="#27ae60" strokeWidth="2.5"/>
        <text x={aX_R+24} y={aY_R+aH_R+22} fontSize="9.5" fill="#27ae60">y₁=x (known)</text>
        <line x1={aX_R+120} y1={aY_R+aH_R+18} x2={aX_R+136} y2={aY_R+aH_R+18} stroke="#2E75B6" strokeWidth="2" strokeDasharray="5,3"/>
        <text x={aX_R+140} y={aY_R+aH_R+22} fontSize="9.5" fill="#2E75B6">y₂=x² (found)</text>
        <line x1={aX_R+240} y1={aY_R+aH_R+18} x2={aX_R+256} y2={aY_R+aH_R+18} stroke="#e07b00" strokeWidth="1.8"/>
        <text x={aX_R+260} y={aY_R+aH_R+22} fontSize="9.5" fill="#e07b00">C₁x+C₂x²</text>
      </svg>
      <div style={S.dCaption}>
        Left: The Euler–Cauchy equation x²y″+xy′+n²y=0 is solved by the substitution y=x^m, converting it to an algebraic equation in m. Complex roots m=±i give oscillatory solutions cos(ln x) and sin(ln x) — the oscillations compress as x increases since the spacing in x grows exponentially. Right: Reduction of order uses a known solution y₁ to find a second independent solution y₂ by setting y=v(x)y₁ and solving a first-order ODE in v′.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function SecondOrderODEs() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Second-Order Ordinary Differential Equations</h1>

      <p style={S.p}>
        A second-order ODE involves the second derivative y″ alongside y′ and y. The general linear form is y″ + P(x)y′ + Q(x)y = g(x). When g(x) = 0 the equation is homogeneous; otherwise it is nonhomogeneous. The general solution of a second-order linear ODE always has two free constants, requiring two initial (or boundary) conditions to determine a unique solution. This page covers the characteristic equation approach for constant-coefficient problems, the phase plane, two methods for finding particular solutions (undetermined coefficients and variation of parameters), forced oscillations and resonance, power series solutions, and the special techniques of reduction of order and the Euler–Cauchy equation.
      </p>

      <InfoBox
        title="Structure of Second-Order Linear ODEs"
        items={[
          "General form: y″ + P(x)y′ + Q(x)y = g(x). Linear means no products y·y′ or (y′)² etc.",
          "Superposition: if y₁ and y₂ solve the homogeneous equation, so does C₁y₁ + C₂y₂ for any constants.",
          "General solution: y = yₕ + yₚ where yₕ = C₁y₁ + C₂y₂ is the homogeneous solution and yₚ is any particular solution.",
          "Wronskian W[y₁,y₂] = y₁y₂′ − y₂y₁′ ≠ 0 guarantees y₁ and y₂ are linearly independent (a fundamental set).",
          "Constant-coefficient homogeneous y″ + 2γy′ + ω₀²y = 0: solved via characteristic equation r² + 2γr + ω₀² = 0.",
          "Three cases: two real roots (overdamped), repeated root (critically damped), complex conjugate roots (underdamped).",
          "IVP for second-order: needs y(x₀) = y₀ AND y′(x₀) = v₀ — two conditions to fix both C₁ and C₂.",
          "BVP: conditions at two different points, e.g. y(0)=0 and y(L)=0 — may have no solution, one solution, or infinitely many.",
        ]}
      />

      <h2 style={S.h2}>Characteristic Roots — Three Damping Cases</h2>
      <DiagramCharacteristicRoots />
      <Table2 rows={[
        ["Characteristic equation","y″+2γy′+ω₀²y=0 → try y=e^(rt) → r²+2γr+ω₀²=0\nDiscriminant Δ = 4(γ²−ω₀²)"],
        ["Overdamped (Δ>0)",       "Two distinct real roots r₁,r₂ = −γ ± √(γ²−ω₀²)\ny = C₁e^(r₁t) + C₂e^(r₂t) — decays without oscillating"],
        ["Critically damped (Δ=0)","Repeated root r = −γ\ny = (C₁+C₂t)e^(−γt) — fastest non-oscillatory return to 0"],
        ["Underdamped (Δ<0)",      "Complex roots r = −γ ± iωd,  ωd = √(ω₀²−γ²)\ny = e^(−γt)(C₁cos(ωd·t)+C₂sin(ωd·t))"],
        ["Undamped (γ=0)",         "Pure imaginary roots r = ±iω₀\ny = C₁cos(ω₀t)+C₂sin(ω₀t) — eternal oscillation"],
        ["Damping ratio ζ",        "ζ = γ/ω₀: ζ>1 overdamped, ζ=1 critical, ζ<1 underdamped\nQuality factor Q = 1/(2ζ) measures sharpness of resonance"],
      ]} />

      <h2 style={S.h2}>Phase Plane Portraits</h2>
      <DiagramPhasePlane />
      <Table2 rows={[
        ["Phase plane",           "Plot y′ vs y (eliminating t) — trajectory shows state evolution\nEach IC (y₀,v₀) starts one trajectory; arrows show direction of t"],
        ["Stable node",           "Overdamped: both eigenvalues real, negative — trajectories enter origin\nApproach along slow eigendirection as t → ∞"],
        ["Stable spiral",         "Underdamped: complex eigenvalues with negative real part\nSpirals inward; angular speed = ωd"],
        ["Centre",                "Undamped: pure imaginary eigenvalues — closed elliptical orbits\nConserves energy; no attractor"],
        ["Unstable cases",        "If Re(r) > 0: node/spiral repels (unstable); Re(r)=0: centre\nFor y″+2γy′+ω₀²y=0 stability ⟺ γ>0 and ω₀²>0"],
        ["Nullclines",            "y′=0 nullcline: y-axis; y″=0 nullcline: −2γy′ = ω₀²y\nUseful for sketching nonlinear phase portraits"],
      ]} />

      <h2 style={S.h2}>Method of Undetermined Coefficients</h2>
      <DiagramUndeterminedCoefficients />
      <Table2 rows={[
        ["When applicable",       "Constant-coefficient ODE; g(x) is polynomial, exponential,\nsin/cos, or any product/sum of these"],
        ["Guess table",           "g=Pₙ(x): yₚ=Qₙ(x) (poly of same degree)\ng=e^αx: yₚ=Ae^αx;  g=cos(βx) or sin(βx): yₚ=Acos+Bsin"],
        ["Product forcing",       "g=e^αx·Pₙ(x): yₚ=e^αx·Qₙ(x)\ng=e^αx·cos(βx): yₚ=e^αx(Acos(βx)+Bsin(βx))"],
        ["Modification rule",     "If any term in the guess solves the homogeneous ODE,\nmultiply the entire guess by x (or x² for double root)"],
        ["Superposition",         "If g = g₁ + g₂, find yₚ₁ for g₁ and yₚ₂ for g₂ separately\nTotal particular solution yₚ = yₚ₁ + yₚ₂"],
        ["Example",               "y″−y′−2y=4xe^x: guess yₚ=(Ax+B)e^x\nSub and match: A=−2, B=−1 → yₚ=(−2x−1)e^x"],
      ]} />

      <h2 style={S.h2}>Variation of Parameters</h2>
      <DiagramVariationOfParameters />
      <Table2 rows={[
        ["Setup",                 "y=u₁(x)y₁+u₂(x)y₂; impose u₁′y₁+u₂′y₂=0\nThis extra condition simplifies y″ and gives a 2×2 system"],
        ["Wronskian W",           "W[y₁,y₂] = y₁y₂′ − y₂y₁′\nAbel's theorem: W = Ce^(−∫P dx) — never zero if y₁,y₂ independent"],
        ["Formulas for u₁′,u₂′", "u₁′ = −y₂g/W   and   u₂′ = y₁g/W\nThen integrate: u₁=∫−y₂g/W dx, u₂=∫y₁g/W dx"],
        ["Vs. undetermined coeff.","VC works for any continuous g(x) — no restriction on form\nUC is simpler when g has the right form; prefer UC when possible"],
        ["Example",               "y″+y=sec(x): W=1, u₁=ln|cosx|, u₂=x\nyₚ=cos(x)ln|cos(x)|+x·sin(x)"],
        ["Limitation",            "Requires closed-form integrals ∫u₁′dx and ∫u₂′dx\nIf integrals can't be computed, use numerical methods"],
      ]} />

      <h2 style={S.h2}>Forced Oscillations and Resonance</h2>
      <DiagramResonance />
      <Table2 rows={[
        ["Steady-state amplitude","A(ω) = F₀/√((ω₀²−ω²)²+(2γω)²)\nPeaks near ω = √(ω₀²−2γ²) (resonant frequency)"],
        ["Resonant frequency",    "ωᵣ = √(ω₀²−2γ²) < ω₀ for underdamped system\nAs γ→0: ωᵣ→ω₀ and peak amplitude → ∞"],
        ["Transient vs steady-state","Full solution = transient (decays) + steady-state (periodic)\nTransient from initial conditions; dies out due to damping"],
        ["Pure resonance (γ=0)",  "ω=ω₀ forces: yₚ = (F₀/2ω₀)t·sin(ω₀t)\nAmplitude grows without bound — linear in t (modification rule)"],
        ["Beats (small γ,Δω→0)", "Near-resonance: y ≈ A(t)·sin(ω₀t), A(t) oscillates slowly\nBeat frequency = |ω−ω₀|; musical and structural phenomenon"],
        ["Q-factor",              "Q = ω₀/(2γ) = peak amplitude / static amplitude\nHigh Q: sharp, tall resonance peak; used in filter design"],
      ]} />

      <h2 style={S.h2}>Power Series Solutions</h2>
      <DiagramPowerSeries />
      <Table2 rows={[
        ["Ordinary point",        "P(x),Q(x) analytic at x₀ — power series solution always exists\nRadius of convergence ≥ distance to nearest singularity"],
        ["Series substitution",   "Assume y=Σaₙ(x−x₀)ⁿ; shift index so all sums over same power\nCollect coefficients of each (x−x₀)ⁿ and set to zero"],
        ["Recurrence relation",   "Gives aₙ₊₂ (or aₙ₊ₖ) in terms of earlier aₙ\nTwo free parameters a₀,a₁ correspond to two independent solutions"],
        ["Frobenius method",      "At regular singular point: try y=xʳΣaₙxⁿ\nIndicial equation gives r; log term may be needed if r₁−r₂ ∈ ℤ"],
        ["Convergence",           "Ratio test on recurrence relation gives radius of convergence\nOften R = distance to nearest singularity of the ODE"],
        ["Classical equations",   "Legendre: (1−x²)y″−2xy′+n(n+1)y=0 → Legendre polynomials Pₙ\nBessel: x²y″+xy′+(x²−ν²)y=0 → Bessel functions Jν, Yν"],
      ]} />

      <h2 style={S.h2}>Euler–Cauchy and Reduction of Order</h2>
      <DiagramReductionEuler />
      <Table2 rows={[
        ["Euler–Cauchy equation",  "x²y″+axy′+by=0 — try y=xᵐ → indicial equation m²+(a−1)m+b=0\nDistinct real, repeated, or complex roots give different forms"],
        ["Euler–Cauchy: complex",  "m=α±iβ → y=xᵅ(C₁cos(β ln x)+C₂sin(β ln x))\nOscillations in ln x — frequency constant in log scale"],
        ["Reduction of order",     "Given one solution y₁, set y=v(x)y₁\nSubstitution reduces to a first-order ODE in w=v′"],
        ["Reduction formula",      "w′+(2y₁′/y₁+P)w=0 → separable in w\nAfter finding w, integrate to get v, then y₂=v·y₁"],
        ["Abel's identity",        "W[y₁,y₂] = C·exp(−∫P dx) — Wronskian found without knowing y₂\nUseful to verify independence or find W when only y₁ is known"],
        ["Liouville's formula",    "y₂=y₁∫(e^(−∫P dx)/y₁²)dx — explicit second solution from y₁\nDirect integration avoids substitution; same as reduction result"],
      ]} />

      <InfoBox
        title="Key Takeaways — Second-Order ODEs"
        items={[
          "The general solution of a 2nd-order linear ODE is y = C₁y₁ + C₂y₂ + yₚ; you need two ICs to determine C₁ and C₂.",
          "The characteristic equation r² + 2γr + ω₀² = 0 classifies motion: overdamped (real roots), critically damped (repeated), underdamped (complex).",
          "Phase plane portraits make stability visible: stable spirals (underdamped), stable nodes (overdamped), centres (undamped).",
          "Undetermined coefficients is quick when g(x) is a polynomial, exponential, or sinusoid — use the modification rule if the guess duplicates the homogeneous solution.",
          "Variation of parameters always works; it expresses yₚ as u₁y₁+u₂y₂ where u₁,u₂ are found by integrating formulas involving the Wronskian.",
          "At resonance (ω=ω₀, γ=0) the particular solution grows as t·sin(ω₀t) — unbounded amplitude. Damping prevents infinite growth but the peak can still be large.",
          "Power series solutions work near ordinary points; the Frobenius method handles regular singular points and recovers classical functions like Legendre polynomials and Bessel functions.",
          "Reduction of order converts the problem of finding a second independent solution into a first-order ODE, using Liouville's formula y₂ = y₁∫(e^(−∫P dx)/y₁²)dx.",
        ]}
      />
    </div>
  );
}